import React, { useState } from 'react';
import { Container, Typography, Box, Grid, Paper, Button } from '@mui/material';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload, 
  faCar, 
  faVolumeUp, 
  faMapMarkedAlt, 
  faStore, 
  faShareAlt 
} from '@fortawesome/free-solid-svg-icons';

// Context
import { useI18n } from '../../context/I18nContext';

const SectionContainer = styled.section`
  padding: 5rem 0;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: var(--border-radius);
  margin: 2rem 0;
`;

const StepsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  margin-top: 4rem;
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 3rem;
  }
`;

const StepCard = styled(Paper)`
  padding: 2rem;
  height: 100%;
  min-height: 280px; /* Garantindo altura mínima igual para todos os cards */
  text-align: center;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  transition: var(--transition);
  position: relative;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  
  .icon {
    font-size: 3rem;
    color: var(--primary-color);
    margin-bottom: 1.2rem;
  }
  
  h3 {
    margin-bottom: 1rem;
    color: var(--primary-color);
    font-weight: 600;
    font-size: 1.3rem;
  }
  
  p {
    color: #666;
    line-height: 1.5;
    flex-grow: 1; /* Permite que o texto ocupe o espaço disponível */
    display: flex;
    align-items: center;
  }
`;

const StepNumber = styled.div`
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--secondary-color);
  color: var(--light-text);
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.1rem;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled(Typography)`
  position: relative;
  display: inline-block;
  margin-bottom: 1.5rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100%;
    height: 3px;
    background-color: var(--primary-color);
  }
`;

const AudioDemo = styled(Paper)`
  padding: 2.5rem;
  margin-top: 5rem;
  text-align: center;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  background-color: white;
`;

const AudioButtons = styled(Box)`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin: 1.5rem 0;
  
  @media (max-width: 600px) {
    flex-wrap: wrap;
  }
`;

const HowItWorksSection: React.FC = () => {
  const { t, language } = useI18n();
  const [currentAudio, setCurrentAudio] = useState<'pt' | 'en' | 'es'>(language as 'pt' | 'en' | 'es');
  
  const handleAudioChange = (lang: 'pt' | 'en' | 'es') => {
    setCurrentAudio(lang);
  };
  
  const steps = [
    {
      number: 1,
      icon: faDownload,
      title: t('step1_title'),
      description: t('step1_desc'),
    },
    {
      number: 2,
      icon: faCar,
      title: t('step2_title'),
      description: t('step2_desc'),
    },
    {
      number: 3,
      icon: faVolumeUp,
      title: t('step3_title'),
      description: t('step3_desc'),
    },
    {
      number: 4,
      icon: faMapMarkedAlt,
      title: t('step4_title'),
      description: t('step4_desc'),
    },
    {
      number: 5,
      icon: faStore,
      title: t('step5_title'),
      description: t('step5_desc'),
    },
    {
      number: 6,
      icon: faShareAlt,
      title: t('step6_title'),
      description: t('step6_desc'),
    },
  ];
  
  return (
    <SectionContainer id="how-works">
      <Container maxWidth="lg">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Box textAlign="center">
            <SectionTitle variant="h2" color="primary">
              {t('how_works_title')}
            </SectionTitle>
          </Box>
          
          <StepsContainer>
            {steps.map((step, index) => (
              <Box sx={{ width: { xs: '100%', sm: '47%', md: '30%' } }} key={step.number}>
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  <StepCard elevation={2}>
                    <StepNumber>{step.number}</StepNumber>
                    <FontAwesomeIcon icon={step.icon} className="icon" />
                    <Typography variant="h5" component="h3">
                      {step.title}
                    </Typography>
                    <Typography variant="body1">
                      {step.description}
                    </Typography>
                  </StepCard>
                </motion.div>
              </Box>
            ))}
          </StepsContainer>
          
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <AudioDemo elevation={2}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" color="primary" sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                  {t('audio_demo_title')}
                  <Box sx={{ position: 'absolute', bottom: -10, left: 0, width: '100%', height: 3, bgcolor: 'primary.main' }} />
                </Typography>
              </Box>
              
              <Typography variant="body1" sx={{ maxWidth: '700px', margin: '0 auto 2rem', color: '#555' }}>
                {t('audio_demo_desc')}
              </Typography>
              
              <AudioButtons>
                <Button
                  variant={currentAudio === 'pt' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => handleAudioChange('pt')}
                  sx={{ px: 2, py: 1 }}
                >
                  {t('audio_pt')}
                </Button>
                
                <Button
                  variant={currentAudio === 'en' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => handleAudioChange('en')}
                  sx={{ px: 2, py: 1 }}
                >
                  {t('audio_en')}
                </Button>
                
                <Button
                  variant={currentAudio === 'es' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => handleAudioChange('es')}
                  sx={{ px: 2, py: 1 }}
                >
                  {t('audio_es')}
                </Button>
              </AudioButtons>
              
              <Box sx={{ width: '100%', maxWidth: '600px', margin: '0 auto', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}>
                <audio
                  controls
                  src={`/assets/audio/taguatinga_${currentAudio}.mp3`}
                  style={{ width: '100%' }}
                  preload="auto"
                  autoPlay={false}
                >
                  Seu navegador não suporta o elemento de áudio.
                </audio>
              </Box>
            </AudioDemo>
          </motion.div>
        </motion.div>
      </Container>
    </SectionContainer>
  );
};

export default HowItWorksSection;
